import { range, sortBy, uniqBy } from 'lodash';
import { computed, observable, makeObservable } from 'mobx';
import { PropsType } from './SportMeetingsList';
import { EventsCollectionList } from 'src/domains/sportsbook/state/eventsCollection/EventsCollectionList.js';
import { calculateRaceStatusFromEventItem } from 'src/domains/sportsbook/utils/filterRacesWithFinishState';
import { DateTime } from 'src_common/utils/time/time';
import { Common } from 'src/domains/common/Common';
import { EventsCollectionState } from 'src/domains/sportsbook/state/eventsCollection/EventsCollectionState';
import { EventListGroupEventItemType } from 'src_common/common/websocket2/modelsApi/EventsCollectionQuery';
import { CompetitionId } from 'src_common/common/websocket2/id/WebsocketId';
import { SportRacesTypeParamType } from 'src/domains/layouts/state/router/newRouter/mainRouteTypes';

export type SportType = 'horseracing' | 'greyhoundracing';

export type EventWithCompetitionIdType = EventListGroupEventItemType & { competitionId: CompetitionId };

export const getEventsList = (common: Common, sport: SportType, isBuilding: boolean): EventsCollectionList | null => {
    const races = EventsCollectionState.get(common).getRacesForWeek(sport);
    const racesForBuilding = races.filterBySportAndEvent((_sport, event): boolean => {
        if (calculateRaceStatusFromEventItem(event) === 'RaceStatusFinished') {
            return false;
        }
        return true;
    });
    if (isBuilding) {
        return racesForBuilding;
    }

    return EventsCollectionState.get(common).getRacesForWeek(sport);
};

export class SportMeetingsListState {
    private readonly props: PropsType;
    @observable private dayNewInner: number | null;
    @observable public isOpenUkAndIreland: boolean = true;
    @observable public isOpenInternational: boolean = true;

    public constructor(
        props: PropsType,
        private readonly common: Common
    ) {
        makeObservable(this);
        this.props = props;
        this.dayNewInner = null;
    }

    public setDays = (day: number): void => {
        this.dayNewInner = day;
    };

    public handleClickUkAndIreland = (): void => {
        this.isOpenUkAndIreland = !this.isOpenUkAndIreland;
    };

    public handleClickInternational = (): void => {
        this.isOpenInternational = !this.isOpenInternational;
    };

    @computed private get dayNew(): number | null {
        const days = this.getDays;

        if (this.dayNewInner === null && days !== null) {
            const firstDay = days[0];

            if (firstDay === undefined) {
                return null;
            }

            return firstDay.unixMs();
        }

        return this.dayNewInner;
    }

    @computed public get getSport(): EventsCollectionList | null {
        return getEventsList(this.common, this.props.sport, this.props.isBuilding);
    }

    @computed public get collectionEvents(): Array<EventWithCompetitionIdType> {
        const eventsList = this.getSport;
        if (eventsList === null) {
            return [];
        }

        const eventsWithCompetition: Array<EventWithCompetitionIdType> = [];
        for (const competition of this.getSport?.competitionsRaw ?? []) {
            const competitionId = this.common.models.id.getCompetitionId(competition.id);
            for (const eventItem of competition.events) {
                eventsWithCompetition.push({
                    ...eventItem,
                    competitionId: competitionId,
                });
            }
        }

        return eventsWithCompetition;
    }

    @computed private get getDays(): Array<DateTime> {
        if (this.getSport === null) {
            return [];
        }
        const startTimesList: Array<number> =
            this.collectionEvents.map((eventModel) => {
                return eventModel.startTime;
            }) ?? [];
        const time = sortBy(startTimesList, (x) => x);

        const start = DateTime.from(time[0])?.startOfDays();
        const end = DateTime.from(time[time.length - 1])?.startOfDays();
        if (start === undefined) {
            console.error('Start time error. Time', time);
            return [];
        }
        const diff = end?.diffInDays(start);

        if (diff === undefined) {
            return [];
        }

        const days = uniqBy(
            range(0, diff + 1).map((x) => start.addDays(x).startOfDays()),
            (x) => x.unixMs()
        );
        return days;
    }

    public hasEventsPerDay = (whatDay: number, filterType: SportRacesTypeParamType | null): boolean => {
        if (this.collectionEvents === null) {
            return false;
        }

        return this.collectionEvents.some((eventModel: EventWithCompetitionIdType) => {
            const eventCountry = eventModel.tags['country'];
            if (filterType === 'uk-ire') {
                if (eventCountry !== 'GB' && eventCountry !== 'IE') {
                    return false;
                }
            }
            if (filterType === 'international') {
                if (eventCountry === 'GB' || eventCountry === 'IE') {
                    return false;
                }
            }
            return DateTime.from(eventModel.startTime)?.isSameDay(DateTime.current().addDays(whatDay)) ?? false;
        });
    };

    @computed public get filterEventsComputed(): EventWithCompetitionIdType[] {
        if (this.dayNew === null) {
            return [];
        }

        return this.collectionEvents.filter((eventModel) => {
            return DateTime.from(eventModel.startTime)?.startOfDays().unixMs() === this.dayNew;
        });
    }

    @computed public get filterEventsComputedUkIreland(): EventWithCompetitionIdType[] {
        return (
            this.filterEventsComputed?.filter(
                (eventModel) => eventModel.tags['country'] === 'GB' || eventModel.tags['country'] === 'IE'
            ) ?? []
        );
    }

    @computed public get filterEventsComputedInternational(): EventWithCompetitionIdType[] {
        return (
            this.filterEventsComputed?.filter(
                (eventModel) => eventModel.tags['country'] !== 'GB' && eventModel.tags['country'] !== 'IE'
            ) ?? []
        );
    }
}
